import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { money, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VehicleBadge } from "@/components/shared";
import { toast } from "sonner";
import { MapPin, Weight, Package, Boxes, Truck, CreditCard, ShieldCheck } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckoutFlow } from "@/components/CheckoutFlow";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CHECKOUT_PUBLIC_KEY = process.env.REACT_APP_CHECKOUT_PUBLIC_KEY;
const AVAILABLE_METHODS = [
  CHECKOUT_PUBLIC_KEY && "card",
  PAYPAL_CLIENT_ID && "paypal",
  "mock",
].filter(Boolean);
// Default to a method that does NOT pre-create the order (paypal/mock create on click);
// card must build a payment session up front, so we only mount it on explicit selection.
const DEFAULT_METHOD = AVAILABLE_METHODS.find((m) => m !== "card") || AVAILABLE_METHODS[0];

const CITY_COORDS = {
  TR: { Istanbul: { lat: 41.02, lng: 28.98 }, Ankara: { lat: 39.93, lng: 32.86 }, Izmir: { lat: 38.42, lng: 27.14 } },
  IL: { "Tel Aviv": { lat: 32.08, lng: 34.78 }, Jerusalem: { lat: 31.78, lng: 35.22 }, Haifa: { lat: 32.79, lng: 34.99 } },
  AE: { Dubai: { lat: 25.2, lng: 55.27 }, "Abu Dhabi": { lat: 24.45, lng: 54.38 }, Sharjah: { lat: 25.35, lng: 55.4 } },
};
const firstCity = (cc) => {
  const m = CITY_COORDS[cc] || CITY_COORDS.TR;
  const name = Object.keys(m)[0];
  return { city: name, ...m[name] };
};

export default function Checkout() {
  const { sid } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useCart();
  const [saved, setSaved] = useState([]);
  const [countryCode, setCountryCode] = useState("TR");
  const [addr, setAddr] = useState({ label: "Home", line1: "", city: "Istanbul", lat: 41.02, lng: 28.98, country_code: "TR" });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(DEFAULT_METHOD);
  const orderRef = useRef(null);

  useEffect(() => {
    api.get(`/stores/${sid}`).then((r) => {
      const cc = r.data.country_code || "TR";
      setCountryCode(cc);
      setAddr((a) => ({ ...a, country_code: cc, ...firstCity(cc) }));
    });
    api.get("/me/addresses").then((r) => setSaved(r.data));
  }, [sid]);

  const cities = CITY_COORDS[countryCode] || CITY_COORDS.TR;

  const getQuote = async () => {
    try {
      const { data } = await api.post("/checkout/quote", { store_id: sid, address: { label: addr.label, line1: addr.line1, city: addr.city, lat: addr.lat, lng: addr.lng, country_code: countryCode } });
      setQuote(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not get estimate"); }
  };

  useEffect(() => { getQuote(); /* eslint-disable-next-line */ }, [addr.lat, addr.lng]);

  const setCity = (c) => setAddr({ ...addr, city: c, ...cities[c] });

  // Create the Market order once (server sets total + 15% commission), reused across PayPal retries.
  const ensureOrder = async () => {
    if (orderRef.current) return orderRef.current;
    const { data: order } = await api.post("/orders", { store_id: sid, address: { label: addr.label, line1: addr.line1, city: addr.city, lat: addr.lat, lng: addr.lng, country_code: countryCode } });
    orderRef.current = order.id;
    await refresh();
    return order.id;
  };

  const placeAndPay = async () => {
    if (!addr.line1) { toast.error("Enter a delivery address"); return; }
    setBusy(true);
    try {
      const oid = await ensureOrder();
      await api.post(`/orders/${oid}/pay`, { method: "mock_card" });
      await refresh();
      toast.success(t("customer.paySuccess"));
      navigate(`/app/orders/${oid}`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-6">
        <h1 className="text-2xl font-display font-bold">Checkout</h1>

        <div className="rounded-2xl border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><MapPin className="w-4 h-4 text-brand" /> Delivery address</div>
          {saved.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {saved.map((a) => (
                <button key={a.id} data-testid={`addr-${a.id}`} onClick={() => setAddr(a)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${addr.line1 === a.line1 ? "border-brand bg-accent" : ""}`}>{a.label} · {a.city}</button>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Label</Label><Input data-testid="addr-label" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div>
              <Label>City</Label>
              <select data-testid="addr-city" value={addr.city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border bg-card text-sm">
                {Object.keys(cities).map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Address line</Label><Input data-testid="addr-line" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Street, building, apt" className="mt-1 rounded-xl" /></div>
        </div>

        {quote && (
          <div className="rounded-2xl border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><Truck className="w-4 h-4 text-brand" /> Smart delivery</div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="rounded-xl bg-muted/50 p-3"><Weight className="w-4 h-4 mx-auto text-muted-foreground" /><div className="font-bold mt-1">{quote.metrics.total_weight_kg}kg</div><div className="text-[11px] text-muted-foreground">Weight</div></div>
              <div className="rounded-xl bg-muted/50 p-3"><Boxes className="w-4 h-4 mx-auto text-muted-foreground" /><div className="font-bold mt-1">{quote.metrics.total_volume_m3}m³</div><div className="text-[11px] text-muted-foreground">Volume</div></div>
              <div className="rounded-xl bg-muted/50 p-3"><Package className="w-4 h-4 mx-auto text-muted-foreground" /><div className="font-bold mt-1">{quote.metrics.total_packages}</div><div className="text-[11px] text-muted-foreground">Packages</div></div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Eligible vehicles ({quote.distance_km} km):</div>
              <div className="flex flex-wrap gap-2">
                {["motorcycle", "car", "van", "truck"].map((v) => (
                  <VehicleBadge key={v} type={v} active={quote.eligible_vehicles.includes(v)} />
                ))}
              </div>
              <div className="text-xs text-brand mt-2 font-medium">Recommended: {quote.recommended_vehicle || "—"}</div>
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-2xl border bg-card p-5 space-y-3 sticky top-20">
          <div className="font-semibold">Order summary</div>
          {quote ? (
            <>
              <Row label={t("common.subtotal")} value={money(quote.subtotal, quote.currency)} />
              <Row label={t("common.deliveryFee")} value={money(quote.delivery_fee, quote.currency)} />
              <Row label={t("common.serviceFee")} value={money(quote.service_fee, quote.currency)} />
              {quote.tax > 0 && <Row label={`${quote.tax_name || t("common.tax")}${quote.tax_inclusive ? " (incl.)" : ""}`} value={money(quote.tax, quote.currency)} />}
              <div className="h-px bg-border my-1" />
              <div className="flex justify-between items-center"><span className="font-semibold">{t("common.total")}</span><span className="font-display font-bold text-xl text-brand">{money(quote.total, quote.currency)}</span></div>

              {AVAILABLE_METHODS.length > 1 && (
                <div data-testid="pay-method-selector" className="pt-1">
                  <div className="text-xs text-muted-foreground mb-2">{t("customer.payMethod")}</div>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_METHODS.map((m) => (
                      <button key={m} type="button" data-testid={`pay-method-${m}`} onClick={() => setMethod(m)}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${method === m ? "border-brand bg-accent font-medium" : "hover:bg-muted/50"}`}>
                        {m === "card" ? t("customer.methodCard") : m === "paypal" ? t("customer.methodPaypal") : t("customer.methodMock")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {method === "card" && (
                <CheckoutFlow
                  ensureOrder={ensureOrder}
                  onSuccess={(oid) => { toast.success(t("customer.paySuccess")); navigate(`/app/orders/${oid}`, { replace: true }); }}
                  locale={i18n.language}
                  addressReady={!!addr.line1}
                />
              )}

              {method === "paypal" && (
                <div data-testid="paypal-checkout" className="pt-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {t("customer.securePay", "Secure payment via PayPal")}</div>
                  {!addr.line1 && <div className="text-xs text-amber-600 mb-2">{t("customer.enterAddress")}</div>}
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: quote.currency, intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", shape: "pill", color: "gold", label: "pay" }}
                      disabled={busy || !addr.line1}
                      forceReRender={[quote.total, quote.currency]}
                      createOrder={async () => {
                        setBusy(true);
                        try {
                          const oid = await ensureOrder();
                          const { data } = await api.post(`/orders/${oid}/paypal/create-order`);
                          return data.paypal_order_id;
                        } catch (e) { setBusy(false); toast.error(formatApiError(e.response?.data?.detail)); throw e; }
                      }}
                      onApprove={async () => {
                        try {
                          await api.post(`/orders/${orderRef.current}/paypal/capture`);
                          toast.success(t("customer.paySuccess"));
                          navigate(`/app/orders/${orderRef.current}`, { replace: true });
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        finally { setBusy(false); }
                      }}
                      onCancel={() => setBusy(false)}
                      onError={() => { setBusy(false); toast.error(t("customer.payError", "Payment error, please retry")); }}
                    />
                  </PayPalScriptProvider>
                </div>
              )}

              {method === "mock" && (
                <>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {t("customer.mockPay")}</div>
                  <Button data-testid="pay-btn" onClick={placeAndPay} disabled={busy} className="w-full h-12 rounded-xl bg-brand hover:bg-brand-dark gap-2">
                    <CreditCard className="w-4 h-4" />{busy ? "…" : `${t("customer.payNow")} ${money(quote.total, quote.currency)}`}
                  </Button>
                </>
              )}
            </>
          ) : <div className="text-sm text-muted-foreground py-6 text-center">{t("customer.enterAddress")}</div>}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>;
}
